import { useEffect, useRef } from "react";

const DEFAULT_VIDEO_SRC =
  "https://raw.githubusercontent.com/Ictraeh/ai_assets_lib/main/Jelly%20Fish%202.mp4";

// Base scene settings
const SETTINGS = {
  bgColor: "#000000",
  bgVideoOpacity: 0.72,
  contrast: 100,

  effectOpacity: 0.5,
  blendMode: "lighter" as string,

  // Base values — interaction will modulate these
  baseResolution: 13,
  baseSizeMultiplier: 1.4,

  minBrightness: 0.0,
  maxBrightness: 0.52,

  // Interactivity — punchier mouse influence
  mouseRadius: 280, // pixels of influence around cursor
  mousePushStrength: 160, // how far shapes get pushed
  mouseGrowStrength: 3.5, // how much bigger shapes near cursor get
  scrollZoomRange: 1.2, // +/- scroll modulation on size + grid
};

// Stable pseudo-random generator (per grid cell)
function pseudoRandom(x: number, y: number): number {
  return Math.abs(Math.sin(x * 12.9898 + y * 78.233) * 43758.5453) % 1;
}

function definePolygon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  sides: number,
): void {
  for (let i = 0; i < sides; i += 1) {
    const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
    if (i === 0) {
      ctx.moveTo(x + radius * Math.cos(angle), y + radius * Math.sin(angle));
    } else {
      ctx.lineTo(x + radius * Math.cos(angle), y + radius * Math.sin(angle));
    }
  }
  ctx.closePath();
}

function defineStar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  innerRadius: number,
  points: number,
): void {
  for (let i = 0; i < points * 2; i += 1) {
    const r = i % 2 === 0 ? radius : innerRadius;
    const angle = (i * Math.PI) / points - Math.PI / 2;
    if (i === 0) {
      ctx.moveTo(x + r * Math.cos(angle), y + r * Math.sin(angle));
    } else {
      ctx.lineTo(x + r * Math.cos(angle), y + r * Math.sin(angle));
    }
  }
  ctx.closePath();
}

function drawShape(
  ctx: CanvasRenderingContext2D,
  type: number,
  x: number,
  y: number,
  size: number,
): void {
  ctx.beginPath();
  const half = size / 2;
  const quarter = size / 4;

  switch (type) {
    case 0:
      ctx.arc(x, y, half, 0, Math.PI * 2);
      break;
    case 1:
      ctx.rect(x - half, y - half, size, size);
      break;
    case 2:
      ctx.moveTo(x, y - half);
      ctx.lineTo(x + half, y + half);
      ctx.lineTo(x - half, y + half);
      break;
    case 3:
      ctx.moveTo(x - half, y - half);
      ctx.lineTo(x + half, y - half);
      ctx.lineTo(x, y + half);
      break;
    case 4:
      ctx.rect(x - half, y - quarter, size, half);
      break;
    case 5:
      ctx.rect(x - quarter, y - half, half, size);
      break;
    case 6:
      ctx.moveTo(x, y - half);
      ctx.lineTo(x + half, y);
      ctx.lineTo(x, y + half);
      ctx.lineTo(x - half, y);
      break;
    case 7:
      definePolygon(ctx, x, y, half, 5);
      break;
    case 8:
      definePolygon(ctx, x, y, half, 6);
      break;
    case 9:
      definePolygon(ctx, x, y, half, 8);
      break;
    case 10:
      defineStar(ctx, x, y, half, half / 2.5, 5);
      break;
    case 11: {
      const t = size / 6;
      ctx.rect(x - t, y - half, t * 2, size);
      ctx.rect(x - half, y - t, size, t * 2);
      break;
    }
    case 12:
      ctx.arc(x, y, half, Math.PI, 0);
      ctx.closePath();
      break;
    case 13:
      ctx.arc(x, y, half, 0, Math.PI * 2);
      ctx.arc(x, y, half / 2, 0, Math.PI * 2, true);
      break;
    default:
      break;
  }

  ctx.fill();
}

export default function Home() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Offscreen canvas for reading pixels
    const offscreenCanvas = document.createElement("canvas");
    const offscreenCtx = offscreenCanvas.getContext("2d", {
      willReadFrequently: true,
    });
    if (!offscreenCtx) return;

    // Video element
    const video = document.createElement("video");
    video.src = DEFAULT_VIDEO_SRC;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;

    // ---- Interactive state ----
    // Mouse target + smoothed mouse position (for nice easing)
    const mouseTarget = { x: -9999, y: -9999, active: false };
    const mouse = { x: -9999, y: -9999 };

    // Scroll progress accumulator (-1 .. 1 typically, clamped)
    let scrollProgress = 0;
    // Smoothed value used in render
    let scrollSmooth = 0;

    const handleMouseMove = (e: MouseEvent) => {
      mouseTarget.x = e.clientX;
      mouseTarget.y = e.clientY;
      mouseTarget.active = true;
    };

    const handleMouseLeave = () => {
      mouseTarget.active = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        mouseTarget.x = e.touches[0].clientX;
        mouseTarget.y = e.touches[0].clientY;
        mouseTarget.active = true;
      }
    };

    const handleTouchEnd = () => {
      mouseTarget.active = false;
    };

    const handleWheel = (e: WheelEvent) => {
      // Prevent page scroll on this fullscreen canvas
      e.preventDefault();
      // Normalize delta and accumulate (clamped to -1..1)
      const delta = e.deltaY * 0.0015;
      scrollProgress = Math.max(-1, Math.min(1, scrollProgress + delta));
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("wheel", handleWheel, { passive: false });

    // Resize handling
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      offscreenCanvas.width = canvas.width;
      offscreenCanvas.height = canvas.height;
    };
    window.addEventListener("resize", resize);
    resize();

    let rafId = 0;

    const animate = () => {
      rafId = requestAnimationFrame(animate);

      // Smooth mouse follow (lerp)
      if (mouseTarget.active) {
        // If first activation, snap close so it doesn't fly in from -9999
        if (mouse.x < -1000) {
          mouse.x = mouseTarget.x;
          mouse.y = mouseTarget.y;
        } else {
          mouse.x += (mouseTarget.x - mouse.x) * 0.18;
          mouse.y += (mouseTarget.y - mouse.y) * 0.18;
        }
      } else {
        // Send mouse off-screen smoothly so influence fades out
        mouse.x = -9999;
        mouse.y = -9999;
      }

      // Smooth scroll
      scrollSmooth += (scrollProgress - scrollSmooth) * 0.08;
      // Slowly decay scrollProgress back toward 0 so effect "settles"
      scrollProgress *= 0.985;

      // Compute interactive parameters based on scroll
      // scrollSmooth -1..1 -> sizeMul scales between /range and *range
      const zoomFactor = Math.pow(SETTINGS.scrollZoomRange, scrollSmooth);
      const dynamicSizeMul = SETTINGS.baseSizeMultiplier * zoomFactor;
      const dynamicRes = Math.max(
        5,
        Math.min(50, Math.round(SETTINGS.baseResolution * zoomFactor)),
      );

      // 1. Reset and draw flat background
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = SETTINGS.bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        // Object-fit: cover dimensions
        const videoRatio = video.videoWidth / video.videoHeight;
        const canvasRatio = canvas.width / canvas.height;

        let drawWidth: number;
        let drawHeight: number;
        let offsetX: number;
        let offsetY: number;

        if (canvasRatio > videoRatio) {
          drawWidth = canvas.width;
          drawHeight = canvas.width / videoRatio;
          offsetX = 0;
          offsetY = (canvas.height - drawHeight) / 2;
        } else {
          drawWidth = canvas.height * videoRatio;
          drawHeight = canvas.height;
          offsetX = (canvas.width - drawWidth) / 2;
          offsetY = 0;
        }

        const contrastStr = `contrast(${SETTINGS.contrast}%)`;

        // 2. Draw original video background
        if (SETTINGS.bgVideoOpacity > 0) {
          ctx.globalAlpha = SETTINGS.bgVideoOpacity;
          ctx.filter = contrastStr;
          ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
          ctx.filter = "none";
        }

        // 3. Draw video to offscreen canvas to analyze pixels
        offscreenCtx.clearRect(0, 0, offscreenCanvas.width, offscreenCanvas.height);
        offscreenCtx.filter = contrastStr;
        offscreenCtx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
        offscreenCtx.filter = "none";

        const imgData = offscreenCtx.getImageData(
          0,
          0,
          canvas.width,
          canvas.height,
        ).data;
        const res = dynamicRes;

        // 4. Set blending parameters for shapes
        ctx.globalAlpha = SETTINGS.effectOpacity;
        ctx.globalCompositeOperation = SETTINGS.blendMode as any;

        const mouseR = SETTINGS.mouseRadius;
        const mouseR2 = mouseR * mouseR;
        const mouseActive = mouseTarget.active;

        // --- Draw cursor ring to make interaction zone obvious ---
        if (mouseActive && mouse.x > -1000) {
          ctx.save();
          ctx.globalCompositeOperation = "source-over";
          ctx.globalAlpha = 1.0;

          // Outer soft glow
          const glow = ctx.createRadialGradient(
            mouse.x,
            mouse.y,
            mouseR * 0.2,
            mouse.x,
            mouse.y,
            mouseR,
          );
          glow.addColorStop(0, "rgba(255,255,255,0.18)");
          glow.addColorStop(0.6, "rgba(255,255,255,0.05)");
          glow.addColorStop(1, "rgba(255,255,255,0)");
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, mouseR, 0, Math.PI * 2);
          ctx.fill();

          // Outer ring (influence radius)
          ctx.strokeStyle = "rgba(255,255,255,0.55)";
          ctx.lineWidth = 1.5;
          ctx.setLineDash([6, 6]);
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, mouseR, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);

          // Inner solid ring
          ctx.strokeStyle = "rgba(255,255,255,0.95)";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, 18, 0, Math.PI * 2);
          ctx.stroke();

          // Center dot
          ctx.fillStyle = "rgba(255,255,255,1)";
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();

          // Restore blend state for shape pass
          ctx.globalAlpha = SETTINGS.effectOpacity;
          ctx.globalCompositeOperation = SETTINGS.blendMode as any;
        }

        for (let y = res / 2; y < canvas.height; y += res) {
          for (let x = res / 2; x < canvas.width; x += res) {
            const pixelX = Math.floor(x);
            const pixelY = Math.floor(y);
            const index = (pixelY * canvas.width + pixelX) * 4;

            const r = imgData[index];
            const g = imgData[index + 1];
            const b = imgData[index + 2];

            const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

            if (
              brightness >= SETTINGS.minBrightness &&
              brightness <= SETTINGS.maxBrightness
            ) {
              let drawX = x;
              let drawY = y;
              let size = brightness * res * dynamicSizeMul;

              // --- Mouse interaction: push shapes away + grow them ---
              if (mouseActive) {
                const dx = x - mouse.x;
                const dy = y - mouse.y;
                const dist2 = dx * dx + dy * dy;
                if (dist2 < mouseR2 && dist2 > 0.01) {
                  const dist = Math.sqrt(dist2);
                  // falloff: 1 at center, 0 at edge
                  const falloff = 1 - dist / mouseR;
                  const falloffEase = falloff * falloff;
                  // Push direction (away from cursor)
                  const nx = dx / dist;
                  const ny = dy / dist;
                  drawX += nx * SETTINGS.mousePushStrength * falloffEase;
                  drawY += ny * SETTINGS.mousePushStrength * falloffEase;
                  // Grow size near cursor
                  size *= 1 + SETTINGS.mouseGrowStrength * falloffEase;
                }
              }

              const gridCol = Math.floor(x / res);
              const gridRow = Math.floor(y / res);
              const shapeType = Math.floor(pseudoRandom(gridCol, gridRow) * 14);

              ctx.fillStyle = `rgb(${r},${g},${b})`;

              if (size > 0.5) {
                drawShape(ctx, shapeType, drawX, drawY, size);
              }
            }
          }
        }
      }
    };

    video.play().catch((err) => {
      console.log("Video autoplay prevented:", err);
    });
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("wheel", handleWheel);
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, []);

  return (
    <div className="fixed inset-0 w-screen h-screen overflow-hidden bg-black">
      <canvas
        ref={canvasRef}
        id="mainCanvas"
        className="absolute top-0 left-0 block w-screen h-screen z-[1] cursor-none"
      />

      {/* Floating tech / marine-bio academia title */}
      <header
        className="pointer-events-none absolute top-0 left-0 right-0 z-[2] px-6 md:px-12 pt-6 md:pt-8 text-white select-none"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {/* Center: main title */}
        <div className="text-center">
          <div
            className="text-[10px] md:text-[11px] tracking-[0.45em] uppercase text-white/60 mb-1"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Bioluminescent Systems — Field Study
          </div>
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-black tracking-[-0.02em] leading-[0.95] text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <span
              className="italic font-medium"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Aequorea
            </span>
            <span className="px-3 md:px-4 text-white/40 font-light">·</span>
            <span className="font-black uppercase">Drift Index</span>
          </h1>
          <div
            className="mt-2 text-[10px] md:text-[11px] tracking-[0.35em] uppercase text-white/55"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            Vol. 06 · Cnidaria Motion · 2026
          </div>
        </div>

        {/* Hair line divider */}
        <div className="mt-5 md:mt-6 h-px w-full bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      </header>
    </div>
  );
}